import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampSelection, MentionObligatoire } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import { useReferentiel } from "@/lib/referentiels";
import { api } from "@/lib/api";

/**
 * Les modèles de document (ADR-0060, ticket #106) : les formulaires métier que LAB
 * compose sans le développeur. Un modèle a un nom, un type de document et des
 * versions ; celle qui sert aux fiches nouvelles est la version publiée d'un
 * modèle en usage. L'écran est celui de l'Administrateur ; le serveur refuse les
 * autres quoi qu'il en soit.
 */
export function PageModelesDocument() {
  const liste = useChargement((signal) => api.modelesDocument(signal), []);
  const [creation, setCreation] = useState(false);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-encre">Modèles de document</h1>
          <p className="mt-1 text-sm text-encre-douce">
            Les formulaires que les fiches rempliront : sections, champs, versions. Une version
            publiée ne change plus ; on en ouvre une nouvelle.
          </p>
        </div>
        {!creation ? (
          <Bouton variante="principal" onClick={() => setCreation(true)}>
            <Plus className="h-4 w-4" aria-hidden />
            Nouveau modèle
          </Bouton>
        ) : null}
      </div>
      {creation ? <FormulaireModele onAnnuler={() => setCreation(false)} /> : null}
      {liste.erreur ? <Erreur message={liste.erreur} onReessayer={liste.recharger} /> : null}
      {!liste.erreur && liste.enCours ? <Chargement message="Chargement des modèles…" /> : null}
      {liste.donnees && liste.donnees.length === 0 ? (
        <Vide
          titre="Aucun modèle"
          detail="LABGEST n'en livre aucun : à l'Administrateur de les composer."
        />
      ) : null}
      {liste.donnees && liste.donnees.length > 0 ? (
        <Carte>
          <ul className="divide-y divide-filet">
            {liste.donnees.map((modele) => (
              <li key={modele.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <Link
                  to={`/administration/modeles-document/${modele.id}`}
                  className="min-w-0 flex-1 break-words text-sm font-medium text-encre underline-offset-2 hover:underline"
                >
                  {modele.nom}
                </Link>
                <span className="text-xs text-encre-douce">{modele.type_libelle}</span>
                {/* Chaque état en toutes lettres (ADR-0024). */}
                <Etiquette ton={modele.version_en_usage ? "actif" : "neutre"}>
                  {!modele.actif
                    ? "Modèle retiré"
                    : modele.version_en_usage
                      ? `Version ${modele.version_en_usage} en usage`
                      : "Aucune version en usage"}
                </Etiquette>
                {modele.brouillon ? (
                  <Etiquette>{`Version ${modele.brouillon} en brouillon`}</Etiquette>
                ) : null}
              </li>
            ))}
          </ul>
        </Carte>
      ) : null}
    </div>
  );
}

function FormulaireModele({ onAnnuler }: { onAnnuler: () => void }) {
  const types = useReferentiel("types-document", { actives: true });
  const navigate = useNavigate();
  const [nom, setNom] = useState("");
  const [type, setType] = useState("");
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  return (
    <Carte>
      <EnTeteCarte>
        <h2 className="text-sm font-semibold text-encre">Nouveau modèle</h2>
      </EnTeteCarte>
      <CorpsCarte>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (evenement) => {
            evenement.preventDefault();
            setEnvoi(true);
            setErreur(null);
            try {
              const modele = await api.creerModeleDocument(nom, type);
              navigate(`/administration/modeles-document/${modele.id}`);
            } catch (cause) {
              setErreur(messageDe(cause));
              setEnvoi(false);
            }
          }}
        >
          <MentionObligatoire />
          {erreur ? <Erreur message={erreur} /> : null}
          <div className="grid gap-3 sm:grid-cols-2">
            <Champ
              libelle="Nom du modèle"
              required
              maxLength={200}
              value={nom}
              onChange={(e) => setNom(e.target.value)}
            />
            <ChampSelection
              libelle="Type de document"
              required
              aide="Il dit ce que les fiches demandent : validation, signatures."
              value={type}
              onChange={(e) => setType(e.target.value)}
              options={[
                {
                  valeur: "",
                  libelle: types.donnees ? "Choisir un type" : "Chargement…",
                },
                ...(types.donnees?.valeurs ?? []).map((v) => ({
                  valeur: v.code,
                  libelle: v.libelle,
                })),
              ]}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Bouton type="submit" variante="principal" taille="compact" disabled={envoi}>
              {envoi ? "Création…" : "Créer le modèle"}
            </Bouton>
            <Bouton
              type="button"
              variante="discret"
              taille="compact"
              onClick={onAnnuler}
              disabled={envoi}
            >
              Annuler
            </Bouton>
          </div>
        </form>
      </CorpsCarte>
    </Carte>
  );
}
